// The master view's front door: every tenant in the registry, whatever its
// status. Stats are their own lazy query per row, so one broken tenant schema
// slows its own cell and nothing else.
import { Building2 } from "lucide-react";
import { useNavigate } from "react-router";
import type { AdminTenant } from "@gb/admin-contracts";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { TenantStatusPill } from "./tenant-status";
import { useTenantStats, useTenants } from "./tenants-api";

function StatsCells({ tenantId }: { tenantId: string }) {
  const stats = useTenantStats(tenantId);
  if (stats.isError) return <span className="text-[0.75rem] text-danger">unavailable</span>;
  if (!stats.data) return <span className="text-[0.75rem] text-faint">…</span>;
  const s = stats.data;
  return (
    <span className="font-mono text-[0.75rem] text-muted">
      {s.users}u · {s.projects}p · {s.documents}d · {s.chatMessages}q
    </span>
  );
}

function LastActivity({ tenantId }: { tenantId: string }) {
  const stats = useTenantStats(tenantId);
  const at = stats.data?.lastActivityAt;
  return (
    <span className="font-mono text-[0.75rem] text-faint">
      {at ? new Date(at).toLocaleDateString() : "—"}
    </span>
  );
}

export function TenantsPage() {
  const tenants = useTenants();
  const navigate = useNavigate();

  return (
    <div>
      <PageHeader
        eyebrow="Backoffice"
        title="Tenants"
        actions={
          <Button size="sm" onClick={() => void navigate("/tenants/new")}>
            New tenant
          </Button>
        }
      />
      <div className="mt-6">
        {tenants.isError ? (
          <EmptyState
            icon={Building2}
            title="Couldn’t load tenants"
            description={String(tenants.error)}
          />
        ) : (
          <DataTable<AdminTenant>
            columns={[
              {
                key: "name",
                header: "Tenant",
                render: (t) => (
                  <div className="min-w-0">
                    <div className="truncate text-[0.9rem] font-semibold text-ink">{t.name}</div>
                    <div className="truncate font-mono text-[0.72rem] text-faint">
                      {t.slug} · {t.emailDomains.join(", ") || "no domains"}
                    </div>
                  </div>
                ),
              },
              {
                key: "status",
                header: "Status",
                width: 130,
                render: (t) => <TenantStatusPill status={t.status} />,
              },
              { key: "stats", header: "Contents", width: 220, render: (t) => <StatsCells tenantId={t.id} /> },
              { key: "activity", header: "Last question", width: 120, render: (t) => <LastActivity tenantId={t.id} /> },
            ]}
            rows={tenants.data ?? []}
            getRowKey={(t) => t.id}
            onSelect={(t) => void navigate(`/tenants/${t.id}`)}
            emptyMessage="No tenants registered."
          />
        )}
      </div>
    </div>
  );
}
